// components/WhyQuestionModal.tsx

import {
  Dialog,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Chip,
  Divider,
  Stack,
} from '@mui/material';

import CloseIcon from '@mui/icons-material/Close';
import LinkIcon from '@mui/icons-material/Link';

interface WeakAreaItem {
  label: string;
  value: string;
}

interface FoundationItem {
  title: string;
  desc: string;
}

interface NextTopicItem {
  label: string;
  icon: string;
}

export interface WhyQuestionPopupContent {
  headerIcon: string;
  headerTitle: string;
  weakAreasTitle: string;
  weakAreasDescription: string;
  weakAreas: WeakAreaItem[];
  foundationTitle: string;
  foundationDescription: string;
  foundationPoints: FoundationItem[];
  nextTitle: string;
  nextDescription: string;
  nextTopics: NextTopicItem[];
  encouragementIcon: string;
  encouragementText: string;
}

const DEFAULT_CONTENT: WhyQuestionPopupContent = {
  headerIcon: '🎯',
  headerTitle: 'Why This Question?',
  weakAreasTitle: 'Targeting Your Weak Areas',
  weakAreasDescription:
    'Based on your recent performance, we noticed you struggled with these concepts:',
  weakAreas: [
    { label: 'Motion & Friction', value: '47%' },
    { label: 'Forces & Drag', value: '50%' },
  ],
  foundationTitle: 'Building Your Foundation',
  foundationDescription:
    'This question helps strengthen your understanding of:',
  foundationPoints: [
    {
      title: 'Shape & Drag Relationship',
      desc: 'Essential for aerodynamics topics ahead',
    },
    { title: 'Fluid Properties', desc: 'Foundation for pressure & buoyancy' },
    {
      title: "Speed's Effect on Friction",
      desc: 'Key for energy & momentum concepts',
    },
  ],
  nextTitle: "What's Coming Next",
  nextDescription: 'Mastering this will prepare you for:',
  nextTopics: [
    { label: 'Aerodynamics & Flight', icon: '✈️' },
    { label: 'Fluid Mechanics', icon: '🌊' },
    { label: 'Energy Conservation', icon: '⚡' },
  ],
  encouragementIcon: '💪',
  encouragementText:
    "You've got this! Every question is a step toward mastery.",
};

type Props = {
  open: boolean;
  onClose: () => void;
  content?: WhyQuestionPopupContent;
};

export default function WhyQuestionPopup({ open, onClose, content }: Props) {
  const popupContent = content ?? DEFAULT_CONTENT;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(17, 24, 39, 0.56)',
          backdropFilter: 'blur(1.5px)',
        },
      }}
      PaperProps={{
        sx: {
          borderRadius: '28px',
          bgcolor: '#ffffff',
          boxShadow: '0 20px 60px rgba(15, 23, 42, 0.35)',
          maxWidth: { xs: 'calc(100% - 24px)', sm: 560 },
          overflow: 'hidden',
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 4.1,
          py: 2.55,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'linear-gradient(100deg, #5f7cf7 0%, #7c56c8 100%)',
          color: 'white',
        }}
      >
        <Stack direction="row" spacing={1.9} alignItems="center">
          <Box component="span" sx={{ fontSize: 27, lineHeight: 1 }}>
            {popupContent.headerIcon}
          </Box>
          <Typography variant="body1" sx={{ fontWeight: 700, fontSize: 16 }}>
            {popupContent.headerTitle}
          </Typography>
        </Stack>

        <IconButton
          size="small"
          onClick={onClose}
          sx={{
            color: 'white',
            width: 40,
            height: 40,
            bgcolor: 'rgba(255,255,255,0.22)',
            borderRadius: '999px',
            '& .close-icon': {
              transition: 'transform 0.35s ease',
            },
            '&:hover': {
              bgcolor: 'rgba(255,255,255,0.32)',
            },
            '&:hover .close-icon': {
              transform: 'rotate(180deg)',
            },
          }}
        >
          <CloseIcon className="close-icon" sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      {/* Content */}
      <DialogContent
        sx={{
          px: 4.1,
          py: 4.1,
          maxHeight: { xs: '64vh', sm: '67vh' },
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: 8,
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: '#9ca3af',
            borderRadius: 999,
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'transparent',
          },
        }}
      >
        <Stack spacing={4.3}>
          {/* Section 1 */}
          <Box>
            <SectionTitle text={popupContent.weakAreasTitle} icon="📊" />

            <Typography variant="body2">
              {popupContent.weakAreasDescription}
            </Typography>

            <Stack
              direction="row"
              spacing={1.9}
              flexWrap="wrap"
              useFlexGap
              mt={2.3}
            >
              {popupContent.weakAreas.map((item) => (
                <TagChip
                  key={`${item.label}-${item.value}`}
                  label={item.label}
                  value={item.value}
                />
              ))}
            </Stack>
          </Box>

          <Divider sx={{ opacity: 0.45, my: 0.6 }} />

          {/* Section 2 */}
          <Box>
            <SectionTitle text={popupContent.foundationTitle} />

            <Typography variant="body2">
              {popupContent.foundationDescription}
            </Typography>

            <Stack spacing={2.35} mt={2.1}>
              {popupContent.foundationPoints.map((item) => (
                <ListItemRow
                  key={`${item.title}-${item.desc}`}
                  title={item.title}
                  desc={item.desc}
                />
              ))}
            </Stack>
          </Box>

          <Divider sx={{ opacity: 0.45, my: 0.6 }} />

          {/* Section 3 */}
          <Box>
            <SectionTitle text={popupContent.nextTitle} icon="🚀" />

            <Typography variant="body2">
              {popupContent.nextDescription}
            </Typography>

            <Stack
              direction="row"
              spacing={1.9}
              flexWrap="wrap"
              useFlexGap
              mt={2.35}
            >
              {popupContent.nextTopics.map((item) => (
                <NextTopicChip
                  key={`${item.label}-${item.icon}`}
                  label={item.label}
                  icon={item.icon}
                />
              ))}
            </Stack>
          </Box>

          <Divider sx={{ opacity: 0.45, my: 0.6 }} />

          <Box
            sx={{
              borderRadius: 3,
              border: '1px solid #86efac',
              bgcolor: '#dcfce7',
              px: 3.3,
              py: 2.5,
            }}
          >
            <Stack direction="row" spacing={1.7} alignItems="center">
              <Box component="span" sx={{ fontSize: 26, lineHeight: 1 }}>
                {popupContent.encouragementIcon}
              </Box>
              <Typography
                variant="body2"
                sx={{ color: '#166534', fontWeight: 600, fontSize: 13 }}
              >
                {popupContent.encouragementText}
              </Typography>
            </Stack>
          </Box>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}

/* --- Section Title --- */

function SectionTitle({ text, icon = '🔗' }: { text: string; icon?: string }) {
  return (
    <Stack direction="row" spacing={1.75} alignItems="center" mb={1.2}>
      {icon === '🔗' ? (
        <LinkIcon sx={{ fontSize: 22, color: '#9ca3af' }} />
      ) : (
        <Box component="span" sx={{ fontSize: 23, lineHeight: 1 }}>
          {icon}
        </Box>
      )}
      <Typography
        variant="body1"
        sx={{ fontWeight: 700, color: '#30343b', fontSize: 16 }}
      >
        {text}
      </Typography>
    </Stack>
  );
}

/* --- Chips --- */

function TagChip({ label, value }: { label: string; value: string }) {
  return (
    <Chip
      label={
        <Stack direction="row" spacing={1.15} alignItems="center">
          <Typography
            variant="body2"
            sx={{ fontWeight: 500, lineHeight: 1.1, fontSize: 12 }}
          >
            {label}
          </Typography>
          <Box
            sx={{
              fontSize: 11,
              fontWeight: 700,
              px: 0.9,
              py: '2px',
              borderRadius: '999px',
              background: '#dc2626',
              color: '#fff',
            }}
          >
            {value}
          </Box>
        </Stack>
      }
      sx={{
        height: 38,
        fontSize: 11,
        borderRadius: '999px',
        bgcolor: '#fff5f5',
        color: '#dc2626',
        border: '1px solid #fca5a5',
        '& .MuiChip-label': {
          px: 1.2,
        },
      }}
    />
  );
}

function NextTopicChip({ label, icon }: { label: string; icon: string }) {
  return (
    <Chip
      label={
        <Stack direction="row" spacing={1.15} alignItems="center">
          <span>{icon}</span>
          <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 12 }}>
            {label}
          </Typography>
        </Stack>
      }
      sx={{
        height: 48,
        borderRadius: 2.5,
        bgcolor: '#ecf8ff',
        border: '1px solid #7dd3fc',
        color: '#0369a1',
        '& .MuiChip-label': {
          px: 1.3,
        },
      }}
    />
  );
}

/* --- List Items --- */

function ListItemRow({ title, desc }: { title: string; desc: string }) {
  return (
    <Stack direction="row" spacing={1.7} alignItems="flex-start">
      <Box
        component="span"
        sx={{
          mt: '2px',
          color: '#22c55e',
          fontSize: 18,
          fontWeight: 700,
          lineHeight: 1,
        }}
      >
        ✓
      </Box>
      <Typography variant="body2" sx={{ lineHeight: 1.7, fontSize: 12.5 }}>
        <Box component="span" sx={{ fontWeight: 700, color: '#2f3338' }}>
          {title}
        </Box>{' '}
        <Box component="span" sx={{ color: '#525761', fontWeight: 500 }}>
          — {desc}
        </Box>
      </Typography>
    </Stack>
  );
}
